import React, { useCallback, useEffect, useState } from 'react';

import FloatingActionBar, { FloatingActionBarSpacer } from '@components/shared/ui/FloatingActionBar';
import useSmartBack from '@hooks/shared/useSmartBack';
import ListPageHeader from '@shared/ListPageHeader';
import { handleApiError } from '@utils/apiErrorHandler';
import { Col, Form, Row, Spin, message } from 'antd';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

import ConnectionPanel from './ConnectionPanel';
import { ModuleNameTag } from './EcAccountTags';
import SettingsPanel from './SettingsPanel';

interface ReferenceOption {
  id: string;
  name?: string;
  [key: string]: unknown;
}

interface EcAccountRecord {
  id?: string;
  merchant_id?: string | null;
  merchant?: { id?: string | null; name?: string | null } | null;
  display_name?: string;
  module_name?: string;
  connection_status?: string;
  enable_fetch_orders?: boolean;
  stock_push_enabled?: boolean;
  stock_percentage?: number;
  credentials?: Record<string, any> | null;
  fetch_schedule?: string[];
  sales_channel_id?: string | null;
  shipping_type_mappings?: Array<Record<string, any>>;
  [key: string]: unknown;
}

interface EcAccountFormProps {
  // ecAccountService 仍是 .js service；鏡像其他 shared form（如 InboundEdit.tsx）的 any fallback。
  ecAccountService: any;
  // salesChannelService / shippingTypeService 由 wrapper 自行透過 context 取得 reference data
  // 並以 salesChannels / shippingTypes props 注入，shared form 本身不再 self-fetch。為了相容
  // 既有 callers 仍接受這兩個 prop（unused，將於未來移除）。
  salesChannelService?: any;
  shippingTypeService?: any;
  role: 'admin' | 'operator';
  mode: 'create' | 'edit';
  // 新增模式專用：建立 ec_account 時要送出的 merchant_id。
  // 編輯模式不接受 — 編輯模式以 record 自帶的 merchant_id 為準，由 wrapper
  // 透過 useRecordMerchantSync 把 OperatorDataContext.currentMerchant 切回去。
  merchantIdForCreate?: string | null;
  // Merchant-scoped reference data injected by wrapper. Shared form stays
  // merchant-agnostic — wrapper is responsible for aligning these to the loaded
  // record's merchant before render, so a multi-merchant operator can't save
  // record-A with sales_channel_id / shipping_type_id from currently-selected
  // merchant B.
  salesChannels: ReferenceOption[];
  shippingTypes: ReferenceOption[];
  // Called once the ec_account is fetched (edit mode only). Wrapper uses this
  // to sync OperatorDataContext.currentMerchant to the record's merchant before
  // ConnectionPanel / SettingsPanel render merchant-scoped dropdowns.
  onEcAccountLoaded?: (ecAccount: EcAccountRecord) => void;
  // True while the wrapper is still switching context to the record's merchant.
  // Gates render so the form never briefly shows the previous merchant's options.
  merchantDataLoading?: boolean;
}

const EcAccountForm: React.FC<EcAccountFormProps> = ({
  ecAccountService,
  salesChannelService: _salesChannelService,
  shippingTypeService: _shippingTypeService,
  role,
  mode,
  merchantIdForCreate,
  salesChannels,
  shippingTypes,
  onEcAccountLoaded,
  merchantDataLoading = false,
}) => {
  const { t } = useTranslation();
  const { id } = useParams<{ id?: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const goBack = useSmartBack();

  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(false);
  const [ecAccount, setEcAccount] = useState<EcAccountRecord | null>(null);
  const [connectionStatus, setConnectionStatus] = useState<string>('never_tested');

  // SettingsPanel 需要 moduleName 驅動「平台是否支援 shipping mapping」邏輯。
  // 在 form 內 watch 而非由 SettingsPanel 自行 watch，避免每次 ConnectionPanel
  // 改變 module_name 都讓 Settings 重新註冊 listener。
  const moduleName = Form.useWatch('module_name', form);

  // 建立模式預設值由 <Form initialValues> 提供（在 Form mount 時就寫入 store，不受
  // SettingsPanel 是否 mount 影響）。此處不再用 setFieldsValue。

  // 載入 EC account（編輯模式）
  useEffect(() => {
    if (mode === 'edit' && id) {
      loadEcAccount();
    }
    // 注意：shippingTypes 改由 wrapper 透過 props 注入（merchant-scoped）。
    // 不再在 form 內自行 fetch shippingTypeService.index()。
  }, [id]);

  // 處理 OAuth callback flash message（建立模式中由 URL 參數帶回訊息）
  useEffect(() => {
    const messageKey = searchParams.get('message_key');
    const msgType = searchParams.get('type');

    if (messageKey) {
      if (msgType === 'success') {
        message.success(t(messageKey));
      } else {
        message.error(t(messageKey));
      }

      // 清除 URL 參數
      searchParams.delete('message_key');
      searchParams.delete('type');
      setSearchParams(searchParams, { replace: true });
    }

    // 建立模式中：若 URL 帶有 account_id 代表 OAuth 返回後需恢復進度
    if (mode === 'create') {
      const accountId = searchParams.get('account_id');
      if (accountId && accountId !== 'new') {
        loadEcAccountById(accountId);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadEcAccount = async () => {
    if (!id) return;
    setInitialLoading(true);
    try {
      const data = await ecAccountService.get(id);
      if (data.success) {
        const record: EcAccountRecord = data.data || {};
        setEcAccount(record);
        // 通知 wrapper：拿到 record 後讓它把 OperatorDataContext.currentMerchant
        // 對齊到該 record 的 merchant（由 useRecordMerchantSync 處理）。shared
        // 本身不再讀 / 寫 merchant context，保持 merchant-agnostic。
        onEcAccountLoaded?.(record);
        setConnectionStatus(record.connection_status || 'never_tested');
        form.setFieldsValue({
          display_name: record.display_name,
          module_name: record.module_name,
          enable_fetch_orders: record.enable_fetch_orders,
          stock_push_enabled: record.stock_push_enabled,
          stock_percentage: record.stock_percentage,
          credentials: record.credentials,
          fetch_schedule: record.fetch_schedule || [],
          sales_channel_id: record.sales_channel_id,
          shipping_type_mappings: record.shipping_type_mappings || [],
        });
      } else {
        handleApiError(data, { t, fallbackMessage: t('common.error.record_not_found') });
        navigate('/ec_accounts');
      }
    } catch (error) {
      console.error('Failed to load ec_account:', error);
      message.error(t('common.message.load_failed'));
    } finally {
      setInitialLoading(false);
    }
  };

  // 建立模式中，從 URL 的 account_id 還原已存在的帳號（OAuth 流程返回）
  const loadEcAccountById = async (accountId: string) => {
    setInitialLoading(true);
    try {
      const data = await ecAccountService.get(accountId);
      if (data.success) {
        const record: EcAccountRecord = data.data || {};
        setEcAccount(record);
        // 即使是 create flow，OAuth 返回後 record 已存在；通知 wrapper 對齊 merchant
        // 以免 ConnectionPanel / SettingsPanel 用錯 merchant 的下拉資料。
        onEcAccountLoaded?.(record);
        setConnectionStatus(record.connection_status || 'never_tested');

        // 確保 credentials 為物件（後端有時回傳 JSON 字串）
        let creds: any = record.credentials;
        if (typeof creds === 'string') {
          try {
            creds = JSON.parse(creds);
          } catch (e) {
            console.error('Failed to parse credentials string:', e);
            creds = {};
          }
        }

        // OAuth 返回時，授權前已強制把 enable_fetch_orders 落 DB 為 false（避免不一致）；
        // 這裡只還原使用者明確輸入的欄位，功能設定區塊保留 <Form initialValues> 的預設
        // （抓取訂單 ON、未選時段），讓使用者在 SettingsPanel 解鎖後直接挑時段儲存。
        form.setFieldsValue({
          display_name: record.display_name,
          module_name: record.module_name,
          credentials: creds,
          sales_channel_id: record.sales_channel_id,
        });
      }
    } catch (error) {
      console.error('Failed to load ec_account by id:', error);
    } finally {
      setInitialLoading(false);
    }
  };

  // 儲存後檢查庫存比例是否合計 >= 100%，若是則顯示警告
  const checkStockPercentageTotal = useCallback(async () => {
    try {
      const listData = await ecAccountService.fetchList();
      if (!listData.success) return;
      const accounts = listData.data || [];
      const total = accounts
        .filter((a: any) => a.connection_status === 'connected' && a.stock_push_enabled)
        .reduce((sum: number, a: any) => sum + (Number(a.stock_percentage) || 0), 0);
      if (total >= 100) {
        message.warning(t('models.ec_account.stock_percentage_overallocation_warning', { total }));
      }
    } catch (e) {
      console.error('Failed to check stock percentage total:', e);
    }
  }, [ecAccountService, t]);

  // 儲存表單（ConnectionPanel 與 handleSubmit 共用），回傳儲存後的 record
  // skipValidation: 測試連線時，ConnectionPanel 已自行驗證左側欄位，跳過整體驗證
  const saveForm = useCallback(async ({ skipValidation = false }: { skipValidation?: boolean } = {}) => {
    const values = skipValidation ? form.getFieldsValue(true) : await form.validateFields();
    setLoading(true);
    try {
      const payload = {
        ...values,
        fetch_schedule: values.fetch_schedule || [],
        // 過濾掉未設定 shipping_type_id 的項目（例如選填的 COD）
        shipping_type_mappings: (values.shipping_type_mappings || []).filter((m: any) => m.shipping_type_id),
        // 測試連線階段 SettingsPanel 仍 locked、使用者尚未挑時段，
        // 強制以 enable_fetch_orders=false 落 DB，避免「開啟但無時段」的不一致紀錄；
        // 連線成功後 SettingsPanel 解鎖，UI 預設 ON 由 initialValues 提供，使用者選完時段再正式儲存。
        ...(skipValidation ? { enable_fetch_orders: false } : {}),
      };

      let data;
      if (!ecAccount?.id) {
        // 新增
        if (role === 'operator' && merchantIdForCreate) {
          data = await ecAccountService.create(merchantIdForCreate, payload);
        } else {
          data = await ecAccountService.create(payload);
        }
      } else {
        // 更新
        data = await ecAccountService.update(ecAccount.id, payload);
      }

      if (data.success) {
        const record: EcAccountRecord = data.data || {};
        setEcAccount(record);
        // 從後端更新 connectionStatus
        if (record.connection_status) {
          setConnectionStatus(record.connection_status);
        }
        return record;
      } else {
        handleApiError(data, { t, fallbackMessage: t('common.message.save_failed') });
        return null;
      }
    } catch (error) {
      message.error(t('common.message.save_failed'));
      return null;
    } finally {
      setLoading(false);
    }
  }, [ecAccount, ecAccountService, form, merchantIdForCreate, role, t]);

  // 右側面板儲存按鈕送出處理
  const handleSubmit = async () => {
    const record = await saveForm();
    if (record) {
      // 若有啟用庫存同步，檢查是否超出分配
      if (record.stock_push_enabled && (record.stock_percentage ?? 0) > 0) {
        await checkStockPercentageTotal();
      }
      message.success(t(mode === 'create' ? 'common.message.create_success' : 'common.message.update_success'));
      navigate('/ec_accounts');
    }
  };

  const basePath = role === 'operator' ? '/operator' : '/admin';
  const modeLabel = t(mode === 'create' ? 'common.button.create' : 'common.button.edit');

  const breadcrumbItems = role === 'operator'
    ? ['ec_accounts', { label: modeLabel }]
    : ['platform', 'platform.ec_accounts', { label: modeLabel }];

  // 標題：編輯模式秀 [平台 Tag] 顯示名稱 · 編輯；建立模式維持麵包屑帶出的「建立」
  const titleNode = (mode !== 'create' && ecAccount) ? (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
      <ModuleNameTag moduleName={ecAccount.module_name} style={{ margin: 0, fontSize: 13 }} />
      <span>{ecAccount.display_name || ecAccount.module_name}</span>
      <span style={{ color: '#999', fontSize: 14, fontWeight: 400 }}>· {modeLabel}</span>
    </span>
  ) : undefined;

  // Render-gate: initialLoading 抓 record / 平台清單；merchantDataLoading 切 context
  // 到 record 的 merchant。整段 form 不能 mount，避免 shipping_type 等下拉先用 header
  // merchant 的清單 render。
  if (initialLoading || merchantDataLoading) {
    return (
      <div>
        <ListPageHeader basePath={basePath} items={breadcrumbItems} hideTitle />
        <div style={{ textAlign: 'center', padding: '50px' }}>
          <Spin size="large" />
        </div>
      </div>
    );
  }

  return (
    <div>
      <ListPageHeader
        basePath={basePath}
        items={breadcrumbItems}
        title={titleNode}
        hideTitle={mode === 'create'}
      />
      <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          autoComplete="off"
          initialValues={{
            enable_fetch_orders: true,
            stock_push_enabled: false,
            stock_percentage: 100,
            fetch_schedule: [],
            shipping_type_mappings: [],
          }}
        >
          <Row gutter={24}>
            <Col span={10}>
              <ConnectionPanel
                form={form}
                mode={mode}
                role={role}
                ecAccount={ecAccount}
                ecAccountService={ecAccountService}
                salesChannels={salesChannels}
                salesChannelsLoading={merchantDataLoading}
                connectionStatus={connectionStatus}
                onSave={saveForm}
                onConnectionStatusChange={setConnectionStatus}
              />
            </Col>
            <Col span={14}>
              <SettingsPanel
                form={form}
                connectionStatus={connectionStatus}
                moduleName={moduleName}
                shippingTypes={shippingTypes}
                ecAccount={ecAccount}
                ecAccountService={ecAccountService}
              />
            </Col>
          </Row>
          <FloatingActionBarSpacer />
        </Form>

      <FloatingActionBar
        role={role}
        onCancel={goBack}
        onSubmit={() => form.submit()}
        loading={loading}
      />
    </div>
  );
};

export default EcAccountForm;
